import { NewOrg } from '@pages/Dashboard/actions/CreateNewOrgPage'
import { OrgCollection } from '@pages/Dashboard/overviews/OrgCollectionsPage/useOrgCollectionsPage'
import { UserData } from '@pages/Dashboard/overviews/UserPage/useUserPage'
import { AllUsersOverview } from '@pages/Dashboard/overviews/UsersOverviewPage/useController'
import { SessionsData } from '@pages/Dashboard/overviews/components/SessionsDataGrid'
import { CompletedOrderRow } from '@pages/Dashboard/reports/CompletedOrdersReportPage'
import { OverdueOrdersRow } from '@pages/Dashboard/reports/OverdueOrdersReportPage'
import { NTEInvoiceRow } from '@pages/Dashboard/searches/NTEInvoiceSearchPage'
import { UserSearchRow } from '@pages/Dashboard/searches/UserSearchPage'
import {
    AssignOrgXeroContactsRes,
    CreateLicenseBody,
    DeleteUserRes,
    FreeHoursSnapshot,
    GroupSummary,
    HAUserSettings,
    HabitatAdminUser,
    Licence,
    MPOrder,
    MPSession,
    Org,
    OrgCollectionFull,
    OrgOverview,
    ReviewBody,
    PODIUM_FLEET_SORT_COLUMNS,
    PodiumDetail,
    PodiumEvent,
    PodiumEventsResult,
    PodiumFleetResult,
    PodiumFleetSortColumn,
    PodiumJobRow,
    PodiumJobsResult,
    PodiumStatus,
    SessionTitle,
    OrgXeroContactsRes,
    SendLicenseInvoicesRes,
    SendOrderInvoicesRes,
    UpdateUserRes,
    UserSearchResponse,
} from '@types'
import ApiInteractor from '@utils/api-interactor.ts'
import { resolveHaServerUrl } from '@utils/haServer'
import {
    GridFilterModel,
    GridPaginationModel,
    GridSortModel,
} from '@mui/x-data-grid'
import { UserOrderRow } from '@pages/Dashboard/overviews/UserPage/OrdersTable.tsx'

const BASE_URL = '/api'
const HA_PROXY_URL = `${BASE_URL}/ha`
const MP_PROXY_URL = `${BASE_URL}/mp`

/**
 * Habitat Admin, called straight from the browser rather than through
 * this app's own server. Everything under `/v2/staff` authorises the
 * signed-in member of staff from their bearer token, so there is
 * nothing left for a proxy to add.
 */
const HA_SERVER_URL = resolveHaServerUrl()
const STAFF_SEARCHES_URL = `${HA_SERVER_URL}/v2/staff/searches`
const STAFF_ACTIONS_URL = `${HA_SERVER_URL}/v2/staff/actions`
const STAFF_OVERVIEWS_URL = `${HA_SERVER_URL}/v2/staff/overviews`

/**
 * Exported because the report pages that download a spreadsheet build
 * their own URL and hand it to `downloadFile`, rather than going
 * through one of the helpers below.
 */
export const STAFF_REPORTS_URL = `${HA_SERVER_URL}/v2/staff/reports`

/** What the org pickers render: an org id and the name to show for it. */
type OrgOption = { id: string; label: string }

const ACTIONS_URL = `${BASE_URL}/actions`
const OVERVIEWS_URL = `${BASE_URL}/overviews`

const MP_UPLOADS_URL = `${MP_PROXY_URL}/uploads`
const MP_ORDERS_URL = `${MP_PROXY_URL}/orders`

const get = ApiInteractor._getWithJwt
const getBlob = ApiInteractor._getBlobWithJwt
const patch = ApiInteractor._patchWithJwt
const post = ApiInteractor._postWithJwt
const del = ApiInteractor._deleteWithJwt

const API = {
    ha: {
        accounting: {
            sendLicenseInvoices: (body: {
                orgId?: string[]
                allOrgs: boolean
                from: string
                to: string
            }) =>
                post<SendLicenseInvoicesRes>(
                    `${HA_PROXY_URL}/v2/accounting/licences/generate-invoice`,
                    body
                ),
            hasXeroToken: () =>
                get<{ hasToken: boolean }>(
                    `${HA_PROXY_URL}/v2/accounting/has-xero-token`
                ),
            xeroAuth: () =>
                get<{ consentUrl: string }>(
                    `${HA_PROXY_URL}/v2/accounting/xero-auth`
                ),
            sendOrderInvoices: (body: {
                orgId: string[]
                from: string
                to: string
            }) =>
                post<SendOrderInvoicesRes>(
                    `${HA_PROXY_URL}/v2/accounting/generate-invoice`,
                    body
                ),
            /**
             * Hits Xero, so only ever called on an explicit user action.
             */
            getOrgXeroContacts: () =>
                get<OrgXeroContactsRes>(
                    `${HA_PROXY_URL}/v2/accounting/org-xero-contacts`
                ),
            assignOrgXeroContacts: (
                assignments: { orgId: string; xeroContactId: string }[]
            ) =>
                post<AssignOrgXeroContactsRes>(
                    `${HA_PROXY_URL}/v2/accounting/org-xero-contacts`,
                    { assignments }
                ),
        },
        groups: {
            getGroup: (groupId: string) =>
                get<GroupSummary>(`${HA_PROXY_URL}/v2/groups/${groupId}`),
        },
        licenses: {
            createUKLicenses: (body: CreateLicenseBody) =>
                post<undefined, CreateLicenseBody>(
                    `${HA_PROXY_URL}/v2/licences`,
                    body
                ),
            getOrgLicenses: (orgId: string, activeOnly: boolean = false) =>
                get<Licence[]>(
                    `${HA_PROXY_URL}/v2/licences/org/${encodeURIComponent(orgId)}?activeOnly=${activeOnly}`
                ),
        },
        podium: {
            /**
             * Staff fleet listing: every Podium across all orgs, paginated. Translates the
             * DataGrid's models into the fleet endpoint's query contract (1-indexed `currentPage`,
             * `perPage`, whitelisted `orderBy`, quick-filter → `search`). The `{ error, result }`
             * envelope is unwrapped to `result` by ApiInteractor, so `value` is the fleet payload.
             */
            getFleet: ({
                paginationModel,
                sortModel,
                filterModel,
                orgId,
                status,
                isOnline,
            }: {
                paginationModel?: GridPaginationModel
                sortModel?: GridSortModel
                filterModel?: GridFilterModel
                orgId?: string
                status?: PodiumStatus[]
                isOnline?: boolean
            }) => {
                const params = new URLSearchParams()

                if (paginationModel) {
                    // Fleet pagination is 1-indexed; the grid's is 0-indexed.
                    params.set('currentPage', String(paginationModel.page + 1))
                    params.set('perPage', String(paginationModel.pageSize))
                }

                // Only forward a sort the server accepts; other columns are display-only.
                const sort = sortModel?.[0]
                if (
                    sort?.sort &&
                    (PODIUM_FLEET_SORT_COLUMNS as readonly string[]).includes(
                        sort.field
                    )
                ) {
                    params.set('orderBy', sort.field as PodiumFleetSortColumn)
                    params.set('order', sort.sort)
                }

                // Substring match on hardware_id / name.
                const search = filterModel?.quickFilterValues?.join(' ').trim()
                if (search) params.set('search', search)

                // Explicit fleet filters (not part of the grid's own model).
                if (orgId) params.set('orgId', orgId)
                if (status?.length) params.set('status', status.join(','))
                if (typeof isOnline === 'boolean') {
                    params.set('isOnline', String(isOnline))
                }

                return get<PodiumFleetResult>(
                    `${HA_PROXY_URL}/v2/podium/fleet?${params.toString()}`
                )
            },
            /**
             * One device, for the detail page. Habitat Admin serialises this through an
             * allow-list, so the claim secrets and IoT identity never reach us; the
             * device-authored JSON (hardware, cameras, microphones) arrives parsed.
             * Answers for decommissioned devices too.
             */
            getDetail: (hardwareId: string) =>
                get<PodiumDetail>(
                    `${HA_PROXY_URL}/v2/podium/${encodeURIComponent(hardwareId)}`
                ),
            /**
             * Per-device event history (the `PodiumEvents` log, 90-day retention): error
             * events the device published plus drift-reconciliation audit rows, newest
             * first, paged.
             */
            getEvents: (
                hardwareId: string,
                opts: {
                    page?: number
                    perPage?: number
                    type?: PodiumEvent['type']
                } = {}
            ) => {
                const params = new URLSearchParams()
                if (opts.page) params.set('page', String(opts.page))
                if (opts.perPage) params.set('perPage', String(opts.perPage))
                if (opts.type) params.set('type', opts.type)
                const query = params.toString()
                return get<PodiumEventsResult>(
                    `${HA_PROXY_URL}/v2/podium/${encodeURIComponent(hardwareId)}/events${query ? `?${query}` : ''}`
                )
            },
            /**
             * Per-device processing jobs (the `PodiumJobs` table), newest device activity
             * first, paged and optionally filtered by status. Rows outlive the device: a
             * wiped station keeps its job history.
             */
            getJobs: (
                hardwareId: string,
                opts: {
                    page?: number
                    perPage?: number
                    /**
                     * One status, or several. Several are sent as a comma-separated list
                     * ("Active" is queued,running) so the filter is applied by the query
                     * and the pagination describes the filtered set.
                     */
                    status?: PodiumJobRow['status'] | PodiumJobRow['status'][]
                } = {}
            ) => {
                const params = new URLSearchParams()
                if (opts.page) params.set('page', String(opts.page))
                if (opts.perPage) params.set('perPage', String(opts.perPage))
                if (opts.status?.length) {
                    params.set('status', [opts.status].flat().join(','))
                }
                const query = params.toString()
                return get<PodiumJobsResult>(
                    `${HA_PROXY_URL}/v2/podium/${encodeURIComponent(hardwareId)}/jobs${query ? `?${query}` : ''}`
                )
            },
            /**
             * Ask the device to retry failed jobs. With an `uploadId` it targets one job and
             * the device treats it as a human override, resetting that job's attempt budget —
             * the escape hatch for a needs-attention job. Without one it retries everything
             * recoverable within the device's own cap.
             *
             * Live intent, not queued: a 503 means the broker was down and the command was
             * NOT delivered (unlike disable/wipe, which persist and re-fire on reconnect).
             */
            retryJobs: (hardwareId: string, uploadId?: string) =>
                post<string | undefined>(
                    `${HA_PROXY_URL}/v2/podium/${encodeURIComponent(hardwareId)}/retry-jobs`,
                    uploadId ? { uploadId } : {}
                ),
            /**
             * Reversibly disable a device — it stays MQTT-reachable so it can be re-enabled.
             * Keyed by hardware_id. Returns 202 (with an "accepted…" result string) when the
             * broker is offline: the intent is persisted and re-fired on the device's reconnect.
             */
            disable: (hardwareId: string) =>
                post<string | undefined>(
                    `${HA_PROXY_URL}/v2/podium/${encodeURIComponent(hardwareId)}/disable`
                ),
            /** Re-enable a disabled device. Status self-heals once the device reports its shadow. */
            enable: (hardwareId: string) =>
                post<string | undefined>(
                    `${HA_PROXY_URL}/v2/podium/${encodeURIComponent(hardwareId)}/enable`
                ),
            /**
             * Factory-wipe a device (irreversible). Records the request + durable intent, then
             * commands the device; the AWS IoT cert is deleted only once the device confirms.
             */
            wipe: (hardwareId: string) =>
                post<string | undefined>(
                    `${HA_PROXY_URL}/v2/podium/${encodeURIComponent(hardwareId)}/wipe`
                ),
        },
        orgs: {
            getOrg: (orgId: string) =>
                get<OrgOverview>(
                    `${HA_PROXY_URL}/v2/orgs/id/${encodeURIComponent(orgId)}`
                ),
            getOrgCollection: (collectionId: string) =>
                get<OrgCollectionFull>(
                    `${HA_PROXY_URL}/v2/org-collections/${collectionId}`
                ),
            getOrgCollections: () =>
                get<OrgCollection[]>(`${HA_PROXY_URL}/v2/org-collections`),
            deleteOrgCollection: (collectionId: string) =>
                del(`${HA_PROXY_URL}/v2/org-collections/${collectionId}`),
            review: (orgId: string, review: ReviewBody) =>
                post<undefined, ReviewBody>(
                    `${HA_PROXY_URL}/v2/orgs/review/${orgId}`,
                    review
                ),
        },
        settings: {
            getUserSettings: (
                userId: string,
                date: string = new Date().toString()
            ) =>
                get<HAUserSettings>(
                    `${HA_PROXY_URL}/v2/services/user/setting/${userId}?date=${date}`
                ),
        },
        users: {
            getOrgUsers: (orgId: string) =>
                get<HabitatAdminUser[]>(
                    `${HA_PROXY_URL}/v2/users/org/${encodeURIComponent(orgId)}`
                ),
        },
    },
    mp: {
        uploads: {
            convertAudio: (uploadId: string) =>
                post(`${MP_UPLOADS_URL}/${uploadId}/convert-audio`),
        },
        orders: {
            download: (orderId: string, type: 'audio' | 'docx' = 'audio') =>
                get<{
                    signedUrl: string
                }>(`${MP_ORDERS_URL}/${orderId}/download?type=${type}`),
        },
        staffUploads: {
            /** Staff-only: presign a download for any upload's media,
             * regardless of ownership or an associated order. */
            getPresignedUrl: (
                uploadId: string,
                asset: 'original' | 'audio' = 'audio'
            ) =>
                get<{ signedUrl: string }>(
                    `${MP_UPLOADS_URL}/${uploadId}/staff-presigned-url?asset=${asset}`
                ),
            /** Staff-only duration correction. Sends only the new duration —
             * never anything else the client might attach to this call. */
            setDuration: (uploadId: string, durationMs: number) =>
                patch(`${MP_UPLOADS_URL}/${uploadId}/duration`, {
                    duration: durationMs,
                }),
        },
        users: {
            /**
             * A user's free recording allowance: what they have used, what
             * they have ordered, and whether ordering more brings it back.
             * Read only — the /self equivalent refreshes the allowance as a
             * side effect of being called.
             */
            getFreeHours: (userId: string) =>
                get<FreeHoursSnapshot>(
                    `${MP_PROXY_URL}/users/${encodeURIComponent(userId)}/free-hours`
                ),
        },
    },
    actions: {
        /**
         * Runs on the console's own backend, like the merge, because it has
         * to pass the merge's checks and those read Messenger Pigeon.
         */
        addPersonalUserToOrg: (body: {
            id: string
            role: string
            org_id: string
            dryRun?: boolean
        }) => post(`${ACTIONS_URL}/add-personal-user-to-org`, body),
        previewAddPersonalUserToOrg: (body: {
            id: string
            role: string
            org_id: string
        }) =>
            post<unknown>(
                `${ACTIONS_URL}/add-personal-user-to-org/preview`,
                body
            ),
        mergeAccounts: (
            sourceId: string,
            destinationId: string,
            dryRun?: boolean
        ) =>
            post<unknown>(`${ACTIONS_URL}/merge-accounts`, {
                sourceId,
                destinationId,
                dryRun,
            }),
        previewMergeAccounts: (sourceId: string, destinationId: string) =>
            post<unknown>(`${ACTIONS_URL}/merge-accounts/preview`, {
                sourceId,
                destinationId,
            }),
        createMessengerPigeonUser: (userId: string) =>
            post<unknown>(`${ACTIONS_URL}/create-messenger-pigeon-user`, {
                userId,
            }),
        cancelPendingPaymentOrders: (userId: string, orderIds: string[]) =>
            post<unknown>(`${ACTIONS_URL}/cancel-pending-payment-orders`, {
                userId,
                orderIds,
            }),
        addNewAdminToOrg: (user: {
            firstName: string
            lastName: string
            email: string
            orgId: string
            role: string
        }) => post<undefined>(`${STAFF_ACTIONS_URL}/users`, user),
        createNewOrg: (org: NewOrg) =>
            post<undefined, NewOrg>(`${STAFF_ACTIONS_URL}/orgs`, org),
        transferUser: (oldOrgId: string, newOrgId: string, userId: string) =>
            post(`${STAFF_ACTIONS_URL}/users/transfer`, {
                oldOrgId,
                newOrgId,
                userId,
            }),
        updateUser: (body: { id: string; org_id?: string; role?: string }) => {
            const { id, ...changes } = body
            return patch<UpdateUserRes>(
                `${STAFF_ACTIONS_URL}/users/${id}`,
                changes
            )
        },
        deleteUser: (userId: string) =>
            del<DeleteUserRes>(`${STAFF_ACTIONS_URL}/users/${userId}`),
    },
    download: {
        fetchFile: (fileUrl: string, accept?: string) =>
            getBlob(fileUrl, accept),
    },
    overviews: {
        allUsers: (body: {
            paginationModel?: GridPaginationModel
            sortModel?: GridSortModel
            filterModel?: GridFilterModel
        }) => post<AllUsersOverview>(`${OVERVIEWS_URL}/all-users`, body),
        getOrdersAndSessions: (orgId: string) =>
            get<{
                sessions: MPSession[]
                orders: MPOrder[]
            }>(
                `${OVERVIEWS_URL}/organizations/${encodeURIComponent(orgId)}/sessions`
            ),
        downloadOrgReport: (orgId: string, fromDate: string, toDate: string) =>
            getBlob(
                `${OVERVIEWS_URL}/organizations/${encodeURIComponent(orgId)}/report-download?fromDate=${fromDate}&toDate=${toDate}`
            ),
        getUserOverview: ({ email, id }: { id?: string; email?: string }) =>
            post<UserData>(`${OVERVIEWS_URL}/user-profile`, { id, email }),
        organizations: ({
            paginationModel,
            sortModel,
            filterModel,
            approval,
        }: {
            paginationModel?: GridPaginationModel
            sortModel?: GridSortModel
            filterModel?: GridFilterModel
            approval?: OrgOverview['review_status']
        }) => {
            const urlParams = new URLSearchParams({
                paginationModel: JSON.stringify(paginationModel),
                sortModel: JSON.stringify(sortModel),
                filterModel: JSON.stringify(filterModel),
            })

            if (approval) {
                urlParams.append('approval', approval)
            }

            return get<{
                rows: Org[]
                total_rows: number
            }>(`${STAFF_OVERVIEWS_URL}/organizations?${urlParams.toString()}`)
        },
        sessions: ({
            userId,
            paginationModel,
            sortModel,
            filterModel,
        }: {
            userId?: string
            paginationModel?: GridPaginationModel
            sortModel?: GridSortModel
            filterModel?: GridFilterModel
        }) => {
            const urlParams = new URLSearchParams({
                paginationModel: JSON.stringify(paginationModel),
                sortModel: JSON.stringify(sortModel),
                filterModel: JSON.stringify(filterModel),
            })

            if (userId) {
                urlParams.append('userId', userId)
            }

            return get<{
                rows: SessionsData[]
                total_rows: number
            }>(`${OVERVIEWS_URL}/sessions?${urlParams.toString()}`)
        },
        /**
         * Session titles for a set of ids, as an id-keyed map. Ids with no session are
         * absent from it. Used to name Podium jobs, which carry only the session id.
         */
        sessionTitles: (sessionIds: string[]) => {
            const params = new URLSearchParams({ ids: sessionIds.join(',') })
            return get<Record<string, SessionTitle>>(
                `${OVERVIEWS_URL}/session-titles?${params.toString()}`
            )
        },
        orders: (body: {
            userId?: string
            email?: string
            paginationModel?: GridPaginationModel
            sortModel?: GridSortModel
            filterModel?: GridFilterModel
        }) =>
            post<{
                rows: UserOrderRow[]
                rowCount: number
            }>(`${OVERVIEWS_URL}/user-profile/orders`, body),
    },
    searches: {
        getUKLicenseHolders: () =>
            get<OrgOption[]>(`${STAFF_SEARCHES_URL}/orgs/uk-licence-holders`),
        getOrgsByCountry: (country: string) =>
            get<OrgOption[]>(
                `${STAFF_SEARCHES_URL}/orgs?country=${encodeURIComponent(country)}`
            ),
        listAllOrgs: () => get<OrgOption[]>(`${STAFF_SEARCHES_URL}/orgs`),
        listOrgUsers: (orgId: string) =>
            get<
                Pick<
                    HabitatAdminUser,
                    'id' | 'first_name' | 'last_name' | 'role'
                >[]
            >(`${STAFF_SEARCHES_URL}/orgs/${orgId}/users`),
        listAllAdmins: () =>
            get<
                {
                    id: string
                    name?: string
                    email?: string
                }[]
            >(`${STAFF_SEARCHES_URL}/admins`),
        listOrgAdmins: (orgId: string) =>
            get<
                Pick<
                    HabitatAdminUser,
                    'id' | 'first_name' | 'last_name' | 'email' | 'role'
                >[]
            >(`${STAFF_SEARCHES_URL}/orgs/${orgId}/admins`),
        countOrgOrders: (orgId: string) =>
            get<{ count: number }>(
                `${STAFF_SEARCHES_URL}/orgs/${orgId}/orders/count`
            ),
        queryUsers: (query: string, limit?: number) => {
            const params = new URLSearchParams({ q: query })
            if (limit) {
                params.set('limit', String(limit))
            }
            return get<UserSearchResponse[]>(
                `${STAFF_SEARCHES_URL}/users?${params.toString()}`
            )
        },
        nteInvoice: (type: 'invoice' | 'school', value: string) =>
            get<NTEInvoiceRow[]>(
                `${STAFF_SEARCHES_URL}/nte-invoices?${type}=${encodeURIComponent(value)}`
            ),
        // The query param is `q`; this previously sent `?=<query>`, so the
        // endpoint rejected every call with "q query parameter required".
        users: (query: string) =>
            get<UserSearchRow[]>(
                `${STAFF_SEARCHES_URL}/users?q=${encodeURIComponent(query)}`
            ),
    },
    reports: {
        completedOrders: (from?: string, to?: string) =>
            get<CompletedOrderRow[]>(
                `${STAFF_REPORTS_URL}/completed-orders?from=${from}&to=${to}`
            ),
        overdueOrders: () =>
            get<OverdueOrdersRow[]>(`${STAFF_REPORTS_URL}/overdue-orders`),
    },
} as const

export default API
